'use strict'

import { isElement, isEmpty, forEach, debounce } from 'lodash';

import containerManager from '../../01-helpers/filter-context/container-manager';
import { get as getPageOptions } from '../../01-helpers/utilities/options';
import { publish } from '../../01-helpers/event-manager';
import { push } from '../../01-helpers/utilities/tag-manager';

import { FILTER_CHANGED } from '../../02-const/event-names';

const attributeNames = {
	dataInputType: 'data-selection-type',
	dataRefresh: 'data-refresh',
	dataValue: 'data-selection-value'
},
	optionNames = {
		nodeId: 'NodeId',
		page: 'Page'
	};

export default class FilterInput {

	constructor(baseElement) {
		if (!isElement(baseElement)) {
			return false;
		}

		this._configure(baseElement);
		this._setup();
	}

	/**
	 * configure the class
	 * @param {any} baseElement
	 */
	_configure(baseElement) {
		this.baseElement = baseElement;
		this.inputs = this.baseElement.querySelectorAll('input');
		this.filterContext = containerManager.getContainer();
	}

	/**
	 * basic setup functions 
	 **/
	_setup() {
		this._setInitialValue();

		this._bindEvents.call(this);

		this.NodeId = getPageOptions(optionNames.nodeId);
		this.Page = parseInt(getPageOptions(optionNames.page));
	}

	/* =================================================== */

	get key() {
		return this.baseElement.getAttribute(attributeNames.dataInputType);
	}

	get selectedInput() {
		return find(this.inputs, (input) => input.checked === true);
	}

	get selectedValue() {
		return this.key in this.filterContext.state
			? this.filterContext.state[this.key]
			: null;
	}

	/* =================================================== */

	_setPush(eventName, type, value, action, label) {
		push({
			'event': 'Filter',
			'eventName': eventName, // GA4
			'facet': type, // GA4
			'filter': value, // GA4 
			'eventCategory': 'Filter',
			'eventAction': action,
			'eventLabel': label
		});
	}

	select(value) {
		// We store this in a const so it doesn't have to do a look-up twice
		// when it is not currently selected.
		const selectedInput = this.selectedInput;

		// It is possible we are managing the initial state, this would mean
		// there is not currently any selected input.
		if (selectedInput && value === this.selectedValue) {
			return;
		}

		const matchingInput = find(
			this.inputs,
			(input) => input.value === value.toString()
		);

		if (!matchingInput) {
			return;
		}

		// Now we are sure that it is not currently selected and we've found
		// the new target to select, we manage the checked states. We do however
		// need to check if `selectedInput` exists, since it is possible it does
		// not when we land here initially.
		if (selectedInput) {
			selectedInput.checked = false;
		}

		matchingInput.checked = true;
	}

	/**
	 * Set initial value for the input element 
	 **/
	_setInitialValue() {
		const key = this.key;

		// When we can find a value inside the filter context, we set this
		// as the components initial value. If we can not find any value
		// in the filter context we try to rely on a default value.
		const valueToSet =
			key in this.filterContext.state
				? this.filterContext.state[key]
				: this.baseElement.getAttribute(attributeNames.dataValue);

		if (!valueToSet) {
			return;
		}

		this.select(valueToSet);
	}

	_updateFilterContext(element) {
		const value = element.value,
			id = element.id;

		let tFilters = this.filterContext.state.Filters;

		if (element.checked) {
			tFilters.push(
				{
					Type: this.key,
					Value: value,
					Id: id,
					Name: element.name
				}
			);
		} else {
			let found = false;
			tFilters.find((item, index) => {
				// if filter is alreay found/removed, we can return;
				if (found) {
					return;
				}
				if (item.Type === this.key && item.Value == value) {
					tFilters.splice(index, 1);
					found = true;
				}
			});
		}

		return tFilters;
	}

	/**
	 * on Change handler 
	 * @param {any} evt
	 */
	_onChangeEventHandler(evt) {
		// get value and update filterContext
		const element = evt.currentTarget || evt.target,
			refresh = element.getAttribute(attributeNames.dataRefresh);

		let tFilters = this._updateFilterContext(element);


		switch (element.type) {
			case 'checkbox':
				if (element.checked) {
					this._setPush('filter_add', this.key, element.name, 'Add', `${element.name}|${this.key}`);
				} else {
					this._setPush('filter_remove', this.key, element.name, 'Remove', `${element.name}|${this.key}`);
				}
				break;
			case 'date':
				this._setPush('filter_add', this.key, element.value, 'Add', `${element.name}|${element.value}`);
				break;
			case 'select':
				this._setPush('filter_add', this.key, element.value, 'Add', `${element.name}|${element.value}`);
				break;
		}

		// Update the filterContext
		this.filterContext.update(
			{
				NodeId: this.NodeId,
				Page: this.Page,
				Filters: tFilters
			},
			{ refresh: refresh === 'true' }
		);

		publish(FILTER_CHANGED);
	}

	_bindEvents() {
		forEach(this.inputs, (input) => {
			input.addEventListener('change', debounce((event) => {
				this._onChangeEventHandler(event)
			}, 250), true
			);

			//if input is checked trigger onchange event
			if (input.checked || (input.type === 'date' && !isEmpty(input.value))) {
				const evt = document.createEvent("HTMLEvents");
				evt.initEvent("change", false, true);
				input.dispatchEvent(evt);
			}
		});
	}
}
